import * as React from 'react';
import { AppComponentProps, SetupStep, SetupStepType, SetupViewProps } from '../../../typescriptHelpers';
import { PossibleDefaultReleaseTypes, ReleaseType } from '../../../types';
import Icon, { FieldBinaryOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
    selectAppSettings,
    selectWasAppCreatedAfterQualitativeOnboarding,
    selectUserIsAdmin,
} from '../../../selectors/selectors';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Radio from 'antd/lib/radio';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Checkbox from 'antd/lib/checkbox';
import { capitalize } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { useForm } from 'antd/lib/form/Form';
import { AutomationTip, HelpTip, InfoTip, setupModuleStyle, WarningTip } from '../shared';
import { AppSettingsUpdatable } from '../../../actions/updatable';
import { tryUpdateAppSettings } from '../../../actions/appSettings';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { globalMessage } from '../../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const releaseTypeVisualizationByType = (releaseType: ReleaseType) => {
    // Stands for version
    const V: React.FC = ({ children }) => {
        return (
            <code
                style={{
                    color: antGrey.grey8,
                    fontSize: 32,
                }}
            >
                {children}
            </code>
        );
    };

    // Stands for highlight
    const H: React.FC = ({ children }) => {
        return <span style={{ color: runwayColors.primary }}>{children}</span>;
    };

    switch (releaseType) {
        case ReleaseType.Major:
            return (
                <>
                    <V>
                        <H>1</H>.0.0
                    </V>
                    <V>
                        <H>2</H>.0.0
                    </V>
                    <V>
                        <H>3</H>.0.0
                    </V>
                </>
            );
        case ReleaseType.Minor:
            return (
                <>
                    <V>
                        1.<H>1</H>.0
                    </V>
                    <V>
                        1.<H>2</H>.0
                    </V>
                    <V>
                        1.<H>3</H>.0
                    </V>
                </>
            );
        case ReleaseType.Point:
            return (
                <>
                    <V>
                        1.0.<H>1</H>
                    </V>
                    <V>
                        1.0.<H>2</H>
                    </V>
                    <V>
                        1.0.<H>3</H>
                    </V>
                </>
            );
        default:
            return null;
    }
};

const SetupViewComponent: React.FC<SetupViewProps> = ({ appId, orgId, onChange }) => {
    const defaultReleaseType = useSelector((state: RootState) => selectAppSettings(state, appId).defaultReleaseType);
    const assumePointReleasesAreHotfixes = useSelector(
        (state: RootState) => selectAppSettings(state, appId).assumePointReleasesAreHotfixes,
    );

    // TODO: modify BE to send the auto detected release type
    const autoDetectedType: ReleaseType = null;

    const [form] = useForm();

    const editAppSettingsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsError = useError(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsSuccess = useUpdated(AppSettingsUpdatable.UpdateVersioningDefaults);

    React.useEffect(() => {
        if (editAppSettingsSuccess) {
            globalMessage.success('Default release type updated!');
            onChange(false);
        }
    }, [editAppSettingsSuccess]);

    React.useEffect(() => {
        if (editAppSettingsError) {
            globalMessage.error(editAppSettingsError);
        }
    }, [editAppSettingsError]);

    const dispatch = useDispatch();
    const editAppSettings = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateVersioningDefaults, {
                orgId,
                appId,
                defaultReleaseType: values.releaseType,
                assumePointReleasesAreHotfixes: values.assumePointReleasesAreHotfixes,
            }),
        );
    };

    const initialValues = {
        releaseType: defaultReleaseType,
        assumePointReleasesAreHotfixes: assumePointReleasesAreHotfixes,
    };

    return (
        <Form
            form={form}
            onFinish={editAppSettings}
            initialValues={initialValues}
            onFieldsChange={() => onChange(true)}
            style={{ ...setupModuleStyle, maxWidth: 574 }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text style={{ color: antGrey.grey8, fontSize: 16, fontWeight: 400 }}>
                    Default release type
                </Typography.Text>
                <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                    {() => {
                        const releaseType = form.getFieldValue('releaseType');
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Form.Item name="releaseType" style={{ flex: 1, marginBottom: 0 }}>
                                    <Radio.Group style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {PossibleDefaultReleaseTypes.map((type) => (
                                            <div key={type}>
                                                <div style={{ position: 'relative' }}>
                                                    <Radio
                                                        style={{
                                                            color: antGrey.grey8,
                                                            fontSize: 16,
                                                            fontWeight: 400,
                                                            backgroundColor: antGrey.grey2,
                                                            position: 'relative',
                                                            zIndex: 1,
                                                        }}
                                                        value={type}
                                                    >
                                                        {capitalize(type)}
                                                    </Radio>
                                                    {releaseType === type && (
                                                        <div
                                                            style={{
                                                                width: '100%',
                                                                height: '50%',
                                                                top: 0,
                                                                borderBottom: '1px dashed',
                                                                borderColor: antGrey.grey6,
                                                                position: 'absolute',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {autoDetectedType === type && (
                                                    <Typography.Text
                                                        style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 400 }}
                                                    >
                                                        Auto-detected from your release history
                                                    </Typography.Text>
                                                )}
                                            </div>
                                        ))}
                                    </Radio.Group>
                                </Form.Item>
                                <div
                                    style={{
                                        height: 164,
                                        width: 140,
                                        backgroundColor: 'white',
                                        border: `1px dashed ${antGrey.grey6}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    {releaseTypeVisualizationByType(releaseType)}
                                </div>
                            </div>
                        );
                    }}
                </Form.Item>
                <Form.Item
                    name="assumePointReleasesAreHotfixes"
                    label="Assume point releases are hotfixes"
                    valuePropName="checked"
                    style={{ paddingTop: 12 }}
                >
                    <Checkbox />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                    {() => (
                        <Button
                            loading={editAppSettingsIsLoading}
                            type="primary"
                            htmlType="submit"
                            icon={<ArrowRightOutlined />}
                        >
                            Save
                        </Button>
                    )}
                </Form.Item>
            </Space>
        </Form>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                <span>
                    Runway helps you plan ahead for your future releases, so we’ll keep a ‘next up’ version ready for
                    work. Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </span>
            </InfoTip>
            <WarningTip>
                Runway uses your release version number to sync up across all tools, so it should be the same
                everywhere. Three point-separated numbers without extensions are required, and new versions must
                increment one of the three segments by ONE (so, we support most flavors of SemVer, some CalVer):
            </WarningTip>
            <div
                style={{
                    display: 'flex',
                    padding: '10px 0',
                    paddingLeft: 26,
                    alignItems: 'start',
                    fontSize: 12,
                }}
            >
                <Icon
                    component={CheckOutlined}
                    style={{ fontSize: 16, marginTop: 3, color: runwayColors.statusGreen, marginRight: 8 }}
                />
                <Space direction="vertical" size={4}>
                    <code>1.2.3</code>
                    <code>1.22.3234</code>
                    <code>2022.6.28</code>
                    <code>22.6.0 → 22.7.0</code>
                </Space>
                <Icon
                    component={CloseOutlined}
                    style={{ fontSize: 16, marginTop: 3, color: runwayColors.statusRed, marginRight: 8 }}
                />
                <Space direction="vertical" size={4}>
                    <code>1.2.3-beta</code>
                    <code>1.2</code>
                    <code>2022.6</code>
                    <code>22.6.2 → 22.6.12</code>
                </Space>
            </div>
            <AutomationTip>
                With Runway, you won’t need to think about bumping and branches anymore — once you’re set up,
                automations will handle bumping your version in code, according to your preferences.
            </AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const DefaultReleaseTypeStep: SetupStep = {
    title: () => 'Confirm your default version increment.',
    description: () =>
        'When planning new releases, Runway will predict upcoming version numbers according to this default setting. The actual version number can be edited manually at any time.',
    icon: () => FieldBinaryOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Default version increment',
    listDescription: 'Major, minor, point?',
    integrationsRecommendationBannerData: null,
    isCompleteSelector: (root, { appId }) => {
        const defaultReleaseType = selectAppSettings(root, appId).defaultReleaseType;
        const isUserAdmin = selectUserIsAdmin(root);

        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);

        const wasVisited = getSetupStepLocalData(appId, SetupStepType.DefaultReleaseType)?.hasBeenVisited;

        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ?
                !!defaultReleaseType && wasVisited
            :   !!defaultReleaseType;
    },
};
